import { hasShootingResultContent } from "./programBuilderUtils";

/**
 * Bozze del modulo risultato della vista atleta.
 *
 * Un atleta scrive i chili fra una serie e l'altra, poi chiude il pannello
 * (X, tocco fuori, «Chiudi») o iOS gli chiude l'app: fino a oggi quei valori
 * vivevano solo nello stato React e sparivano senza dire niente, e lui a fine
 * seduta giurava di aver «segnato tutto». Qui si decide due cose, in un posto
 * solo:
 *
 * 1. **quando un modulo contiene qualcosa** (`resultFormHasInput`): le righe
 *    delle serie e i dettagli del superset portano anche indici e nomi, che
 *    non sono input dell'atleta, quindi si guardano solo i campi che compila
 *    lui;
 * 2. **dove sopravvive la bozza** (`localStorage`, per account): chiave per
 *    esercizio della giornata (`programId_dayId_exerciseId`, la stessa del
 *    modulo), con scadenza, perche' una bozza di tre settimane fa non deve
 *    ricomparire su una seduta nuova.
 *
 * `localStorage` puo' mancare o lanciare (finestra privata, dati bloccati):
 * ogni accesso e' in try/catch e senza storage la bozza resta solo in memoria,
 * come prima.
 */

const STORAGE_PREFIX = "kraveon:result-drafts:";
const DRAFTS_VERSION = 1;
const MAX_AGE_MS = 72 * 60 * 60 * 1000;
const MAX_DRAFTS = 40;

// Campi che compila l'atleta. `__missing_fields` e' un segnale del modulo,
// non un valore.
const SCALAR_FIELDS = [
	"weight_kg",
	"reps",
	"reps_text",
	"rpe",
	"score",
	"time_min",
	"time_sec",
	"time_or_reps_input",
	"emom_score_text",
	"free_score",
	"avg_heart_rate",
	"max_heart_rate",
	"notes",
	"scaling",
];
const ROW_FIELDS = ["weight_kg", "reps", "rpe", "notes"];

const filled = (value) =>
	value !== null && value !== undefined && String(value).trim() !== "";

const rowHasInput = (row) =>
	Boolean(row) &&
	typeof row === "object" &&
	ROW_FIELDS.some((field) => filled(row[field]));

/** Vero se l'atleta ha scritto almeno un valore nel modulo. */
export function resultFormHasInput(form) {
	if (!form || typeof form !== "object") return false;
	if (SCALAR_FIELDS.some((field) => filled(form[field]))) return true;
	if (
		Array.isArray(form.running_interval_scores) &&
		form.running_interval_scores.some(filled)
	)
		return true;
	if (
		Array.isArray(form.result_set_rows) &&
		form.result_set_rows.some(rowHasInput)
	)
		return true;
	if (
		Array.isArray(form.superset_details) &&
		form.superset_details.some(rowHasInput)
	)
		return true;
	if (
		form.schema_values_raw &&
		typeof form.schema_values_raw === "object" &&
		Object.values(form.schema_values_raw).some(filled)
	)
		return true;
	if (hasShootingResultContent(form.shooting)) return true;
	return false;
}

const isBrowser = () => typeof window !== "undefined";

const storageKey = (ownerId) => `${STORAGE_PREFIX}${ownerId}`;

const readRaw = (ownerId) => {
	if (!isBrowser() || !ownerId) return null;
	let raw;
	try {
		raw = localStorage.getItem(storageKey(ownerId));
	} catch {
		return null;
	}
	if (!raw) return null;
	let parsed;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	if (!parsed || parsed.v !== DRAFTS_VERSION || !parsed.items) return null;
	return parsed;
};

const writeRaw = (ownerId, items) => {
	if (!isBrowser() || !ownerId) return;
	try {
		if (Object.keys(items).length === 0) {
			localStorage.removeItem(storageKey(ownerId));
		} else {
			localStorage.setItem(
				storageKey(ownerId),
				JSON.stringify({ v: DRAFTS_VERSION, items }),
			);
		}
	} catch {
		// Storage pieno o bloccato: la bozza resta in memoria.
	}
};

const liveItems = (ownerId, now = Date.now()) => {
	const parsed = readRaw(ownerId);
	if (!parsed) return {};
	const items = {};
	for (const [key, entry] of Object.entries(parsed.items)) {
		if (!entry || typeof entry !== "object") continue;
		if (typeof entry.ts !== "number") continue;
		if (now - entry.ts > MAX_AGE_MS || now < entry.ts - MAX_AGE_MS) continue;
		if (!resultFormHasInput(entry.form)) continue;
		items[key] = entry;
	}
	return items;
};

/**
 * Le bozze ancora valide di questo account: `{ [chiave modulo]: form }`.
 * Quelle scadute o vuote si scartano in lettura.
 */
export function readResultDrafts(ownerId, now = Date.now()) {
	const items = liveItems(ownerId, now);
	const out = {};
	for (const [key, entry] of Object.entries(items)) out[key] = entry.form;
	return out;
}

/**
 * Salva (o toglie, se il modulo e' tornato vuoto) la bozza di un esercizio.
 * Tiene al massimo `MAX_DRAFTS` bozze, le piu' recenti.
 */
export function writeResultDraft(ownerId, key, form, now = Date.now()) {
	if (!ownerId || !key) return;
	const items = liveItems(ownerId, now);
	if (resultFormHasInput(form)) {
		const { __missing_fields, ...clean } = form;
		items[key] = { ts: now, form: clean };
	} else {
		delete items[key];
	}
	const trimmed = Object.fromEntries(
		Object.entries(items)
			.sort((a, b) => b[1].ts - a[1].ts)
			.slice(0, MAX_DRAFTS),
	);
	writeRaw(ownerId, trimmed);
}

/** Toglie la bozza di un esercizio: dopo un salvataggio o una cancellazione. */
export function clearResultDraft(ownerId, key, now = Date.now()) {
	if (!ownerId || !key) return;
	const items = liveItems(ownerId, now);
	if (!(key in items)) return;
	delete items[key];
	writeRaw(ownerId, items);
}
